import {useState} from 'react'
import {Button, Modal, Badge, Spinner, Table} from 'react-bootstrap'
import {useMutation, useQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {GET_ONE_PDF_ENVELOPE} from '../../../gql/queries/pdfEnvelopesApiQueries'
import {RESEND_PDF_ENVELOPE_SIGNER, VOID_PDF_ENVELOPE} from '../../../gql/mutations/pdfEnvelopesMutations'
import {NodePdfEnvelope, NodePdfEnvelopeSigner} from '../../../types/pdfEnvelopesApi'
import {BadgeStatus} from './BadgeStatus'
import {parseId} from '../../../helpers'
import {STORAGE_NAMES} from '../../../helpers/consts'
import moment from 'moment'
import {URL as BACKEND_URL} from '../../../gql/client'

interface Props {
  envelopeId: string
  isOpen: boolean
  onClose: () => void
  refetch?: () => void
}

export const ModalEnvelopePreview = ({envelopeId, isOpen, onClose, refetch}: Props) => {
  const [downloading, setDownloading] = useState(false)
  const [showVoidConfirm, setShowVoidConfirm] = useState(false)

  const {data, loading, refetch: refetchEnvelope} = useQuery(GET_ONE_PDF_ENVELOPE, {
    variables: {id: parseId(envelopeId)},
    skip: !envelopeId,
    fetchPolicy: 'no-cache',
  })

  const [voidEnvelope, {loading: voiding}] = useMutation(VOID_PDF_ENVELOPE)
  const [resendSigner, {loading: resending}] = useMutation(RESEND_PDF_ENVELOPE_SIGNER)

  const envelope: NodePdfEnvelope | null = data?.pdfEnvelopeById ?? null

  const pendingSigners = envelope?.signers.filter(
    (s) => s.status === 'PENDING' || s.status === 'VIEWED'
  ) ?? []
  const signedSigners = envelope?.signers.filter((s) => s.status === 'SIGNED') ?? []

  const handleVoidConfirmed = async () => {
    try {
      const {data} = await voidEnvelope({variables: {envelopeId: parseId(envelopeId)}})
      if (data?.pdfEnvelopeVoid?.ok) {
        toast.success('Envelope voided')
        setShowVoidConfirm(false)
        refetchEnvelope()
        refetch?.()
      } else {
        toast.error(data?.pdfEnvelopeVoid?.errorMessage || 'Error voiding')
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const handleResend = async (signer: NodePdfEnvelopeSigner) => {
    try {
      const {data} = await resendSigner({
        variables: {signerId: parseId(signer.id), sendEmail: true},
      })
      if (data?.pdfEnvelopeSignerResend?.ok) {
        toast.success(`Email resent to ${signer.recipientName}`)
      } else {
        toast.error(data?.pdfEnvelopeSignerResend?.errorMessage || 'Error resending')
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const [downloadingExternal, setDownloadingExternal] = useState(false)

  const _fetchAndDownload = async (path: string, filename: string) => {
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    const url = `${BACKEND_URL}${path}`
    const res = await fetch(url, {headers: token ? {Authorization: `Bearer ${token}`} : {}})
    if (!res.ok) throw new Error(`Error ${res.status}`)
    const blob = await res.blob()
    const objectUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    link.click()
    window.URL.revokeObjectURL(objectUrl)
  }

  const handleDownload = async () => {
    if (!envelope) return
    setDownloading(true)
    try {
      await _fetchAndDownload(
        `/pdf-envelopes/${parseId(envelope.id)}/download/`,
        `${envelope.title.replace(/\s+/g, '_')}.pdf`
      )
    } catch (e: any) {
      toast.error(`Error downloading: ${e.message}`)
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadExternal = async () => {
    if (!envelope) return
    setDownloadingExternal(true)
    try {
      await _fetchAndDownload(
        `/pdf-envelopes/${parseId(envelope.id)}/download-external/`,
        `${envelope.title.replace(/\s+/g, '_')}_external.pdf`
      )
    } catch (e: any) {
      toast.error(`Error downloading: ${e.message}`)
    } finally {
      setDownloadingExternal(false)
    }
  }

  return (
    <>
      <Modal show={isOpen} onHide={onClose} size='xl' backdrop='static'>
        <Modal.Header closeButton>
          <Modal.Title>
            {envelope?.title ?? 'Envelope'}{' '}
            {envelope && <BadgeStatus state={envelope.status} />}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <div className='d-flex justify-content-center py-4'>
              <Spinner animation='border' />
            </div>
          )}
          {envelope && (
            <>
              {/* Signers status */}
              <h6 className='fw-bold mb-2'>Signers</h6>
              <Table bordered size='sm' className='mb-4'>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Signed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {envelope.signers.map((s, i) => (
                    <tr key={s.id}>
                      <td>{i + 1}</td>
                      <td>{s.recipientName}</td>
                      <td>
                        <Badge bg='secondary'>{s.role}</Badge>
                      </td>
                      <td>{s.recipientEmail ?? '—'}</td>
                      <td>
                        <BadgeStatus state={s.status} />
                      </td>
                      <td>{s.signedOn ? moment(s.signedOn).format('DD MMM YY HH:mm') : '—'}</td>
                      <td>
                        {s.status !== 'SIGNED' && s.status !== 'VOIDED' && (
                          <Button
                            size='sm'
                            variant='outline-primary'
                            disabled={resending}
                            onClick={() => handleResend(s)}
                          >
                            <i className='bi bi-send' /> Resend
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Activity log */}
              {envelope.logs && envelope.logs.length > 0 && (
                <>
                  <h6 className='fw-bold mb-2'>Activity log</h6>
                  <Table bordered size='sm' className='mb-3' style={{fontSize: 12}}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Action</th>
                        <th>Contact</th>
                        <th>IP</th>
                        <th>Location</th>
                        <th>Browser</th>
                      </tr>
                    </thead>
                    <tbody>
                      {envelope.logs.map((log) => (
                        <tr key={log.id}>
                          <td>{log.created ? moment(log.created).format('DD MMM YY HH:mm') : '—'}</td>
                          <td>
                            <Badge bg='light' text='dark'>
                              {log.action}
                            </Badge>
                          </td>
                          <td>{log.contact ?? '—'}</td>
                          <td style={{whiteSpace: 'nowrap'}}>{log.ipAddress ?? '—'}</td>
                          <td style={{whiteSpace: 'nowrap'}}>{log.region ?? '—'}</td>
                          <td>{log.browser ? log.browser.substring(0, 40) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {envelope?.status === 'COMPLETED' && (
            <>
              <Button variant='success' onClick={handleDownload} disabled={downloading}>
                {downloading
                  ? <Spinner size='sm' animation='border' />
                  : <><i className='bi bi-file-earmark-pdf' /> Download (Full log)</>
                }
              </Button>
              <Button variant='outline-success' onClick={handleDownloadExternal} disabled={downloadingExternal}>
                {downloadingExternal
                  ? <Spinner size='sm' animation='border' />
                  : <><i className='bi bi-file-earmark-arrow-down' /> Download (Client view)</>
                }
              </Button>
            </>
          )}
          {envelope && !['VOIDED', 'EXPIRED', 'COMPLETED'].includes(envelope.status) && (
            <Button variant='danger' onClick={() => setShowVoidConfirm(true)} disabled={voiding}>
              Void Envelope
            </Button>
          )}
          <Button variant='secondary' onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Void confirmation modal */}
      <Modal
        show={showVoidConfirm}
        onHide={() => setShowVoidConfirm(false)}
        centered
        backdrop='static'
        style={{zIndex: 1060}}
      >
        <Modal.Header className='bg-danger text-white'>
          <Modal.Title style={{fontSize: 16}}>
            <i className='bi bi-exclamation-triangle-fill me-2' />
            Void Envelope — are you sure?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{fontSize: 14}}>
            You are about to <strong>permanently cancel</strong> this envelope.
            This action <strong>cannot be undone</strong>. The following will happen immediately:
          </p>
          <ul style={{fontSize: 14, marginBottom: 12}}>
            <li>
              The envelope will be marked as <strong>VOIDED</strong> and no further signatures
              will be accepted.
            </li>
            {pendingSigners.length > 0 && (
              <li>
                The following signers <strong>will lose access</strong> to their signing link —
                their link will show as invalid if they try to open it:
                <ul className='mt-1'>
                  {pendingSigners.map((s) => (
                    <li key={s.id}>
                      <strong>{s.recipientName}</strong>
                      {s.recipientEmail ? ` (${s.recipientEmail})` : ''} — currently{' '}
                      <strong>{s.status}</strong>
                    </li>
                  ))}
                </ul>
              </li>
            )}
            {signedSigners.length > 0 && (
              <li>
                The {signedSigners.length} signer(s) who already signed (
                {signedSigners.map((s) => s.recipientName).join(', ')}) will remain in the
                activity log, but the document will <strong>not</strong> be considered
                fully executed.
              </li>
            )}
            <li>
              The activity log and all recorded events will be preserved for reference.
            </li>
          </ul>
          <div
            className='alert alert-warning d-flex align-items-start gap-2 mb-0'
            style={{fontSize: 13}}
          >
            <i className='bi bi-info-circle-fill mt-1' />
            <span>
              If you need the document signed, you will have to send a <strong>new envelope</strong>{' '}
              from the template.
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowVoidConfirm(false)} disabled={voiding}>
            Cancel — keep the envelope active
          </Button>
          <Button variant='danger' onClick={handleVoidConfirmed} disabled={voiding}>
            {voiding
              ? <Spinner size='sm' animation='border' />
              : <><i className='bi bi-x-circle me-1' />Yes, void this envelope</>
            }
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
